import React, { useRef, useMemo } from "react";
import BrandCarousel from "~/Brands/ExpandedFeatureGroupCarousel/BrandCarousel";
import Item from "~/Brands/ExpandedFeatureGroupCarousel/Item";
import Icon from "~/common/Icons/Standard";
import { CategoryEnhancedGridResult } from "~/common/Store/models/Category";
import setWindowLocationHref from "~/common/navigation/setWindowLocationHref";
import connectUserAndChat, { User } from "~/common/Store/connectUserAndChat";
import UseIntersectionObserver from "~/productListing/UseIntersectionObserver";
import { addBrandLogoAdToSessionStorage } from "~/productListing/addAdsToSessionStorage";
import PromotedTag from "~/common/PromotedTag/PromotedTag";
import { trackAdService } from "~/common/services/AdvertisementTracking/trackAdService";
import { BrandLogoAdRequest } from "~/common/services/AdvertisementTracking/types";

interface Props {
  enhancedGridResult: CategoryEnhancedGridResult;
  user: User;
  mainCategoryId: number;
}

const EnhancedCarousel = ({
  enhancedGridResult,
  user,
  mainCategoryId,
}: Props) => {
  const { showBackground, showDescriptions, sections, description } =
    enhancedGridResult;
  const request: BrandLogoAdRequest = {
    brandId: -1,
    userId: Number(user?.index) || null,
    url: "",
    categoryId: mainCategoryId,
  };

  const promotedSection = sections?.find((section) => section.isPromoted);

  if (promotedSection !== undefined) {
    request.brandId = promotedSection.brandId || -1;
    request.url = promotedSection.link;
  }

  const observerCallback = () =>
    trackAdService("brandlogo", "impression", request);

  const adRef = useRef<HTMLAnchorElement>(null);

  const handleAdClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    let targetHref = promotedSection?.link ?? "";

    if (e.currentTarget instanceof HTMLAnchorElement) {
      targetHref = e.currentTarget.href;
    }

    e.preventDefault();
    await trackAdService("brandlogo", "click", request);
    addBrandLogoAdToSessionStorage(request.brandId, request.categoryId);

    setWindowLocationHref(targetHref);
  };

  UseIntersectionObserver(adRef.current, 0.8, observerCallback);

  const carouselWrapperStyle = "mx-0 relative w-full xs:-mx-2-1/2 xs:w-auto";
  const containerWrapperStyle = `mx-0 pt-4 w-full px-8 xs:pt-6 ${
    showBackground ? "bg-gray-50" : "bg-white"
  }`;

  const defaultPerPage = {
    3: "w-1/3",
    4: "xs:w-1/4",
    5: "md:w-1/5",
    6: "lt:w-1/6",
    7: "lg:w-[14.285%]",
    8: "xl:w-[12.5%]",
    9: "xxl:w-[11.111%]",
  };

  // Filter out styles based on section length
  const perPageStyles = useMemo(() => {
    const styles: string[] = [];
    for (const [key, value] of Object.entries(defaultPerPage)) {
      if (sections.length >= Number(key)) {
        styles.push(value);
      }
    }
    return styles.join(" ");
  }, [sections.length, defaultPerPage]);

  const listContentStyle = `p-2 xs:mr-0 xs:last:mr-0 ${
    promotedSection !== undefined ? "pt-6" : ""
  }`;

  const listWrapperStyle = `flex mb-0 ml-0 relative overflow-hidden ${
    sections.length <= 4 ? "lz:justify-center" : "justify-between"
  }`;

  const iconStyle =
    "cursor-pointer h-8 fill-current text-green-400 hover:text-green-600 w-8";

  const buttonStyle =
    "bg-transparent border-0 cursor-auto h-full mx-0 focus:outline-1 focus:outline-offset-[-7px] absolute top-0 inline-block";

  const anchorStyle = "flex flex-col items-center self-center pb-2 w-full";

  const paragraphStyle =
    "font-semibold text-sm leading-none mb-0 mt-2-1/2 text-center";

  const titleStyle = "text-lg mb-3 text-center xs:text-2xl xs:leading-6";

  return (
    <div className="border-gray-200 border-solid border border-t-0 border-r-0 border-l-0 border-b w-full xs:border-0">
      <div className={carouselWrapperStyle}>
        <div
          className={containerWrapperStyle}
          data-testid="enhanced-carousel-background"
        >
          {showDescriptions && description.trim().length > 0 && (
            <h2 className={titleStyle}>{description}</h2>
          )}
          <BrandCarousel
            slideRight={({ onClick }) => (
              <button
                aria-label="scroll carousel forward"
                className={`pr-2 right-0 ${buttonStyle}`}
                type="button"
                onClick={onClick}
              >
                <Icon name="right-open" className={iconStyle} />
              </button>
            )}
            slideLeft={({ onClick }) => (
              <button
                aria-label="scroll carousel backward"
                className={`pl-2 left-0 ${buttonStyle}`}
                type="button"
                onClick={onClick}
              >
                <Icon name="left-open" className={iconStyle} />
              </button>
            )}
            listWrapperProps={{
              className: listWrapperStyle,
            }}
          >
            {sections?.map(
              (section) =>
                section && (
                  <Item
                    key={section.link}
                    listItemProps={{
                      className: `${listContentStyle} ${perPageStyles}`,
                    }}
                  >
                    <a
                      {...(section.isPromoted ? { ref: adRef } : {})}
                      href={section.link}
                      className={anchorStyle}
                      data-testid="anchor"
                      {...(section.isPromoted
                        ? { onClick: handleAdClick }
                        : {})}
                    >
                      <img
                        className="rounded flex-grow w-full"
                        alt=""
                        src={section.imagePath}
                        data-testid="image"
                        loading="lazy"
                      />
                      {showDescriptions && (
                        <>
                          {section.isPromoted && (
                            <span className="absolute right-1 top-0 items-center px-2 py-1 flex">
                              <PromotedTag />
                            </span>
                          )}
                          <p
                            className={`${paragraphStyle} line-clamp-2`}
                            style={{ maxWidth: "11rem" }}
                          >
                            {section.description}
                          </p>
                        </>
                      )}
                    </a>
                  </Item>
                ),
            )}
          </BrandCarousel>
        </div>
      </div>
    </div>
  );
};

export default connectUserAndChat(EnhancedCarousel);
